// Single source of truth for age divisions, performance categories and
// footwear. Used by individual registration, the school portal and profile.
// The age and soft-shoe rules themselves live in src/shared/ageRules.js so
// the server (api/_lib/ensembleRules.js) applies exactly the same ones.
import { getActiveSeason } from './seasons';
import * as ageRules from '../shared/ageRules';

export const ADULT_DIVISION = 'Adult (18+)';
export const NOT_ELIGIBLE_DIVISION = 'Age not eligible';

export const COMPETITIVE_CATEGORY = 'Classical Variation - Competitive';
export const RECREATIONAL_CATEGORY = 'Classical Variation - Recreational';

export const PERFORMANCE_CATEGORIES = [
  { value: COMPETITIVE_CATEGORY, label: COMPETITIVE_CATEGORY },
  { value: RECREATIONAL_CATEGORY, label: RECREATIONAL_CATEGORY },
];

export const FOOTWEAR_OPTIONS = [
  { value: 'pointe', label: 'Pointe shoes' },
  { value: 'soft', label: 'Soft shoes (ballet slippers)' },
];

export const { POINTE_EXPECTED_FROM_AGE, SOFT_SHOE_WARNING } = ageRules;

/** Age as of Jan 1 of the competition year. See src/shared/ageRules.js. */
export const ageAsOf = (dateOfBirth, year = getActiveSeason().year) => ageRules.ageAsOf(dateOfBirth, year);

export const getAgeDivision = (dateOfBirth, year = getActiveSeason().year) => {
  if (!dateOfBirth) return '';
  const age = ageAsOf(dateOfBirth, year);
  if (age >= 7 && age <= 9) return 'Ages 7-9';
  if (age >= 10 && age <= 11) return 'Ages 10-11';
  if (age >= 12 && age <= 14) return 'Ages 12-14';
  if (age >= 15 && age <= 17) return 'Ages 15-17';
  if (age >= 18) return ADULT_DIVISION;
  return NOT_ELIGIBLE_DIVISION;
};

export const isAdultDivision = (division) => division === ADULT_DIVISION;

export const isMinor = (dateOfBirth, year = getActiveSeason().year) =>
  ageAsOf(dateOfBirth, year) < 18;

/**
 * Footwear to pre-fill for a school student: soft under 12, pointe from 12
 * (age on Jan 1). '' for adults (they choose) or an unknown birthday.
 */
export const suggestedFootwear = (dateOfBirth, year = getActiveSeason().year) => {
  if (!dateOfBirth) return '';
  const age = ageAsOf(dateOfBirth, year);
  if (age >= 18) return '';
  return age >= POINTE_EXPECTED_FROM_AGE ? 'pointe' : 'soft';
};

/** Recreational is adult-only; youth dancers compete in Competitive. */
export const categoriesForDivision = (division) =>
  isAdultDivision(division) ? PERFORMANCE_CATEGORIES : [PERFORMANCE_CATEGORIES[0]];

/**
 * The soft-shoe warning for one dancer's entry, or null. Youth entries only:
 * adults may dance in either footwear. Never blocks the entry.
 */
export const softShoeWarning = ({ dateOfBirth, footwear, isAdultEntry, year = getActiveSeason().year }) =>
  ageRules.softShoeWarning({ dateOfBirth, footwear, isAdultEntry, year });
